/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { initAxios } from '../utils/axiosHelper';
import { parseErrorMsg } from '../utils/errorHelper';

/**
 * Team usage metrics.
 *
 * `timeframe` is parsed server-side the same way for every call:
 *   - 'last month'                 -> the previous calendar month
 *   - '2025-06-01 to 2025-08-15'   -> that range, end date inclusive
 *
 * `team` scopes each call. For the Postgres-backed calls (databases,
 * analyzers) it matches `group_id`; for saved work (cases, Mongo) it
 * matches `groupName`. Pass `cur_user.selected_team.id` from GlobalContext.
 *
 * Backing:
 *   - getUniqueUsers -> public.find_unique_users_by_team()        (init.sql)
 *   - getDatabaseMetrics -> public.team_database_item_counts()   (init.sql)
 *   - getAnalyzerMetrics -> public.team_analyzer_run_summary()   (init.sql)
 *   - getSavedWorkMetrics -> Mongo aggregation over the `cases` collection
 */

export interface UserMetric {
  user_id: string;
  username: string;
  user_first_name: string;
  user_last_name: string;
  last_activity_date: string;
}

export type DatabaseCategory =
  | 'entity'
  | 'tariff'
  | 'productImport'
  | 'productExport';

export interface DatabaseMetric {
  database_id: string;
  database_name: string;
  team_name: string;
  database_type: 'people & companies' | 'products' | 'tariffs';
  // 'imports' | 'exports' for 'products' rows (Products Database vs. Export
  // Hub's ExportDatabase), null for the other database types.
  database_subtype: 'imports' | 'exports' | null;
  database_category: DatabaseCategory;
  item_count: number;
}

export interface AnalyzerMetric {
  team_name: string;
  total_runs: number;
  total_entries: number;
}

export interface SavedWorkMetric {
  type: string;
  caseCount: number;
  timesRun: number;
}

export interface MetricsResponse<T> {
  success: boolean;
  result: T;
}

export interface TeamRef {
  id: string;
  name: string;
}

export interface MetricsOverview {
  timeframe: string;
  teamCount: number;
  users: UserMetric[];
  databases: Record<DatabaseCategory, { count: number; items: number }>;
  analyzers: { totalRuns: number; totalEntries: number };
  savedWork: SavedWorkMetric[];
}

export const MetricsAPI = {
  /**
   * Aggregated overview across one or many teams in a single request (avoids
   * the N-teams x 4-calls client fan-out). group_id-scoped metrics use
   * team.id; unique users uses team.name. Backed by POST /metrics/overview.
   */
  getOverview: async (teams: TeamRef[], timeframe: string) =>
    (await initAxios())
      ?.post('/metrics/overview', JSON.stringify({ teams: teams, timeframe: timeframe }))
      .then((resp) => {
        return {
          success: true,
          result: resp.data as MetricsOverview,
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Unique users on a team who were active within the timeframe, with each
   * user's most recent activity date. Matches KeycloakEventLogs.teams
   * (comma-separated). Backed by public.find_unique_users_by_team().
   */
  getUniqueUsers: async (team: string, timeframe: string) =>
    (await initAxios())
      ?.get('/metrics/users', { params: { team: team, timeframe: timeframe } })
      .then((resp) => {
        return {
          success: true,
          result: resp.data as UserMetric[],
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Databases for a team with an item count per database, where the count
   * comes from DatabaseEntity / DatabaseProduct / DatabaseTariff based on the
   * Database.type. Backed by public.team_database_item_counts().
   */
  getDatabaseMetrics: async (team: string, timeframe: string) =>
    (await initAxios())
      ?.get('/metrics/databases', { params: { team: team, timeframe: timeframe } })
      .then((resp) => {
        return {
          success: true,
          result: resp.data as DatabaseMetric[],
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * ACE analyzer summary for a team: total runs and total run-line entries
   * across those runs. Backed by public.team_analyzer_run_summary().
   */
  getAnalyzerMetrics: async (team: string, timeframe: string) =>
    (await initAxios())
      ?.get('/metrics/analyzers', { params: { team: team, timeframe: timeframe } })
      .then((resp) => {
        return {
          success: true,
          result: resp.data as AnalyzerMetric[],
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),

  /**
   * Saved work (cases) rundown by type for a team: how many cases of each
   * type and how many times they have been run (size of the results[]
   * re-screen history). Backed by a Mongo aggregation over `cases`.
   */
  getSavedWorkMetrics: async (team: string, timeframe: string) =>
    (await initAxios())
      ?.get('/metrics/saved-work', { params: { team: team, timeframe: timeframe } })
      .then((resp) => {
        return {
          success: true,
          result: resp.data as SavedWorkMetric[],
        };
      })
      .catch((err) => {
        return {
          success: false,
          result: parseErrorMsg(err),
        };
      }),
};

export default MetricsAPI;
